import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  Loader2,
  Mic,
  Paperclip,
  Pause,
  Play,
  Send,
  Smile,
  Trash2,
  X,
} from "lucide-react";
import EmojiPicker, { EmojiClickData, EmojiStyle, Theme } from "emoji-picker-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { captureError } from "@/lib/sentry";
import { SEND_MESSAGE, SET_TYPING, CONVERSATION_MESSAGES } from "./queries";
import { useChat } from "./ChatContext";
import {
  uploadChatFile,
  ChatUploadError,
  inferKindFromContentType,
  pastedImageFile,
  ALLOWED_MIME_TYPES,
  MAX_FILE_BYTES,
  type UploadResult,
} from "./uploader";
import { fakeWaveform, formatDuration, VOICE_BAR_COUNT } from "./VoiceNotePlayer";

interface Props {
  conversationId: string;
}

interface PendingAttachment extends UploadResult {
  kind: "image" | "pdf" | "document" | "voice";
  // Local object URL for the image thumbnail in the preview chip (#1003).
  // Revoked when the attachment is removed, replaced, or successfully sent.
  previewUrl?: string;
}

const MAX_VOICE_DURATION = 60;

type VoiceMode = "idle" | "recording" | "preview";

interface VoicePreview {
  blob: Blob;
  url: string;
  durationSec: number;
  mimeType: string;
  fileExtension: string;
  waveform: number[];
}

// Every failed attachment send reaches Sentry, grouped by what was sent and the
// step that failed, with the file's type and size (#943: chat uploads never worked in
// production, and the only sign was an "Upload failed." toast we never saw).
// The server's refusal when a tutor's profile staff disabled keeps a conversation closed (#1754). It is
// a code the page translates (`chat.restricted`), not a failure to report.
const RESTRICTED_CODE = "MESSAGING_RESTRICTED";
const isRestricted = (error: unknown) => error instanceof Error && error.message === RESTRICTED_CODE;

// Whether the main pointer is a finger (a phone or a tablet without a mouse).
function isTouchScreen(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
}

function reportSendFailure(
  error: unknown,
  what: "file" | "voice",
  step: "upload" | "send",
  file?: { type: string; size: number },
) {
  captureError(
    error,
    {
      step,
      contentType: file?.type,
      byteSize: file?.size,
      status: error instanceof ChatUploadError ? error.status : undefined,
    },
    ["chat-send-failed", what, step],
  );
}

/**
 * Pick the best MediaRecorder MIME at runtime. Browsers vary:
 *  - Chrome/Edge/Firefox: prefer audio/webm;codecs=opus
 *  - Safari (16+): prefer audio/mp4;codecs=mp4a.40.2 (AAC-LC)
 *  - Fallback: plain audio/webm with the browser's default codec
 *
 * Returns the chosen MIME plus a matching file extension. The extension
 * matters because R2/ActiveStorage stores the content-type from the
 * uploaded File, and the playback browser sniffs the URL extension when
 * the content-type is missing.
 */
function pickRecorderMime(): { mimeType: string; ext: string } {
  if (typeof MediaRecorder === "undefined") {
    return { mimeType: "audio/webm", ext: "webm" };
  }
  const candidates: Array<{ mimeType: string; ext: string }> = [
    { mimeType: "audio/webm;codecs=opus", ext: "webm" },
    { mimeType: "audio/mp4;codecs=mp4a.40.2", ext: "m4a" },
    { mimeType: "audio/webm", ext: "webm" },
    { mimeType: "audio/mp4", ext: "m4a" },
    { mimeType: "audio/ogg;codecs=opus", ext: "ogg" },
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c.mimeType)) return c;
  }
  return { mimeType: "", ext: "webm" }; // browser default
}

async function sampleBlobWaveform(blob: Blob): Promise<number[]> {
  try {
    const buf = await blob.arrayBuffer();
    const ctx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const decoded = await ctx.decodeAudioData(buf);
    const channel = decoded.getChannelData(0);
    const blockSize = Math.max(1, Math.floor(channel.length / VOICE_BAR_COUNT));
    const bars: number[] = [];
    for (let i = 0; i < VOICE_BAR_COUNT; i++) {
      let max = 0;
      for (let j = 0; j < blockSize; j++) {
        const v = Math.abs(channel[i * blockSize + j] ?? 0);
        if (v > max) max = v;
      }
      bars.push(max);
    }
    const peak = Math.max(...bars, 0.001);
    return bars.map((v) => v / peak);
  } catch {
    return fakeWaveform(`local-${Date.now()}`);
  }
}

export function MessageComposer({ conversationId }: Props) {
  const { t } = useTranslation();
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState<PendingAttachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const emojiPanelRef = useRef<HTMLDivElement | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  const lastTypingPingRef = useRef(0);
  const { replyTarget, setReplyTarget } = useChat();

  // Voice state machine
  const [voiceMode, setVoiceMode] = useState<VoiceMode>("idle");
  const [recordingSec, setRecordingSec] = useState(0);
  const [preview, setPreview] = useState<VoicePreview | null>(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingChosenMimeRef = useRef<{ mimeType: string; ext: string } | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  const [sendMessage, { loading }] = useMutation(SEND_MESSAGE, {
    refetchQueries: [{ query: CONVERSATION_MESSAGES, variables: { conversationId, limit: 50 } }],
  });
  const [setTyping] = useMutation(SET_TYPING);

  // Auto-grow textarea up to a cap
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  }, [body]);

  // Close emoji picker on outside click
  useEffect(() => {
    if (!emojiOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (emojiPanelRef.current?.contains(target)) return;
      if (emojiButtonRef.current?.contains(target)) return;
      setEmojiOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [emojiOpen]);

  // Clean up the preview blob URL when discarded or replaced
  useEffect(() => {
    if (!preview) return;
    return () => URL.revokeObjectURL(preview.url);
  }, [preview]);

  // Revoke a still-attached image's thumbnail URL on unmount. While mounted,
  // revocation happens at the moment of remove/replace/send instead (not the
  // voice preview's effect-cleanup pattern): onSend clears the attachment
  // optimistically, and an effect keyed on it would revoke the URL a failed
  // send still needs when it restores the draft.
  const attachmentPreviewUrlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    attachmentPreviewUrlRef.current = attachment?.previewUrl;
  });
  useEffect(
    () => () => {
      if (attachmentPreviewUrlRef.current) URL.revokeObjectURL(attachmentPreviewUrlRef.current);
    },
    [],
  );

  const stopTyping = () => {
    if (isTypingRef.current) {
      isTypingRef.current = false;
      setTyping({ variables: { input: { conversationId, isTyping: false } } });
    }
  };

  const handleChange = (value: string) => {
    setBody(value);
    if (value.length > 0) {
      // Heartbeat: re-emit `true` at most every 2s while actively typing so the
      // receiver's auto-hide timeout keeps getting refreshed. A single one-shot
      // emit let the indicator vanish mid-typing on long messages.
      const now = Date.now();
      if (!isTypingRef.current || now - lastTypingPingRef.current > 2000) {
        isTypingRef.current = true;
        lastTypingPingRef.current = now;
        setTyping({ variables: { input: { conversationId, isTyping: true } } });
      }
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(stopTyping, 3000);
  };

  // One validate-and-upload path for the attach button and paste (#944), so
  // the size/type rules can never drift apart between the two.
  const attachFile = async (file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      toast.error(t("chat.composer.fileTooLarge"));
      return;
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error(t("chat.composer.fileTypeNotAllowed"));
      return;
    }
    setUploading(true);
    try {
      const result = await uploadChatFile(file);
      const previewUrl =
        file.type.startsWith("image/") && typeof URL.createObjectURL === "function"
          ? URL.createObjectURL(file)
          : undefined;
      setAttachment((prev) => {
        if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
        return { ...result, kind: inferKindFromContentType(result.contentType), previewUrl };
      });
    } catch (error) {
      reportSendFailure(error, "file", "upload", file);
      toast.error(t("chat.composer.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const discardAttachment = () => {
    setAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await attachFile(file);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const image = pastedImageFile(e.clipboardData);
    if (!image) return;
    // No preventDefault: when text rode along with the image (copying from a
    // web page), the text still pastes; an image-only paste inserts nothing.
    void attachFile(image);
  };

  const onEmojiSelect = (data: EmojiClickData) => {
    setBody((b) => b + data.emoji);
    handleChange(body + data.emoji);
  };

  // ─── Voice recording ─────────────────────────────────────────────────

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chosen = pickRecorderMime();
      recordingChosenMimeRef.current = chosen;

      const recorder = chosen.mimeType
        ? new MediaRecorder(stream, { mimeType: chosen.mimeType })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const finalMime = chosen.mimeType || recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: finalMime });
        const url = URL.createObjectURL(blob);
        const waveform = await sampleBlobWaveform(blob);
        const capturedSec = recordingSecRef.current;
        setPreview({
          blob,
          url,
          durationSec: Math.max(1, capturedSec),
          mimeType: finalMime,
          fileExtension: chosen.ext,
          waveform,
        });
        setVoiceMode("preview");
        setRecordingSec(0);
      };

      // Request frequent dataavailable so the blob isn't lost if the
      // tab closes mid-recording (1s slice).
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setVoiceMode("recording");
      setRecordingSec(0);
      recordingSecRef.current = 0;
      recordingTimerRef.current = setInterval(() => {
        recordingSecRef.current += 1;
        setRecordingSec(recordingSecRef.current);
        if (recordingSecRef.current >= MAX_VOICE_DURATION) {
          stopRecording();
        }
      }, 1000);

      // Stop the textarea-level typing indicator if the user was typing
      stopTyping();
      // Close emoji picker if open
      setEmojiOpen(false);
    } catch {
      toast.error(t("chat.composer.micAccessDenied"));
      setVoiceMode("idle");
    }
  };

  // Use a ref so the interval callback always sees the latest value
  // without re-setting itself on every render.
  const recordingSecRef = useRef(0);

  const stopRecording = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
  };

  const cancelRecording = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      // Hook into onstop to discard the resulting blob
      recorder.onstop = () => {
        recorder.stream.getTracks().forEach((t) => t.stop());
      };
      recorder.stop();
    }
    chunksRef.current = [];
    setRecordingSec(0);
    recordingSecRef.current = 0;
    setVoiceMode("idle");
  };

  const discardPreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setPreviewPlaying(false);
    setPreviewProgress(0);
    setVoiceMode("idle");
  };

  const togglePreviewPlay = () => {
    const el = previewAudioRef.current;
    if (!el) return;
    if (previewPlaying) {
      el.pause();
    } else {
      el.play().catch(() => {
        toast.error(t("chat.composer.voicePreviewPlayFailed"));
      });
    }
  };

  const sendVoice = async () => {
    if (!preview || voiceUploading) return;
    setVoiceUploading(true);
    const file = new File(
      [preview.blob],
      `voice-${Date.now()}.${preview.fileExtension}`,
      { type: preview.mimeType },
    );
    let step: "upload" | "send" = "upload";
    try {
      const upload = await uploadChatFile(file);
      step = "send";
      const result = await sendMessage({
        variables: {
          input: {
            conversationId,
            attachmentSignedId: upload.signedId,
            attachmentKind: "voice",
            voiceDurationSeconds: preview.durationSec,
            ...(replyTarget ? { replyToMessageId: replyTarget.id } : {}),
          },
        },
      });
      const errors = result.data?.sendMessage?.errors ?? [];
      if (result.data?.sendMessage?.errorCode === RESTRICTED_CODE) throw new Error(RESTRICTED_CODE);
      if (errors.length > 0) throw new Error(errors.join(", "));
      setReplyTarget(null);
      discardPreview();
    } catch (error) {
      // The recording stays in the preview, so the user can retry.
      if (isRestricted(error)) {
        toast.error(t("chat.restricted"));
      } else {
        reportSendFailure(error, "voice", step, file);
        toast.error(t("chat.composer.voiceSendFailed"));
      }
    } finally {
      setVoiceUploading(false);
    }
  };

  // ─── Send (text / file) ───────────────────────────────────────────────

  const onSend = async () => {
    if ((!body.trim() && !attachment) || loading) return;
    const text = body;
    const att = attachment;
    setBody("");
    setAttachment(null);
    setEmojiOpen(false);
    stopTyping();
    try {
      const result = await sendMessage({
        variables: {
          input: {
            conversationId,
            ...(text.trim() ? { body: text } : {}),
            ...(replyTarget ? { replyToMessageId: replyTarget.id } : {}),
            ...(att ? { attachmentSignedId: att.signedId, attachmentKind: att.kind } : {}),
          },
        },
      });
      const errors = result.data?.sendMessage?.errors ?? [];
      if (result.data?.sendMessage?.errorCode === RESTRICTED_CODE) throw new Error(RESTRICTED_CODE);
      if (errors.length > 0) throw new Error(errors.join(", "));
      setReplyTarget(null);
      // The attachment went out; its local thumbnail URL is no longer needed.
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
    } catch (error) {
      // Put the draft back so a refused send loses nothing.
      setBody((current) => current || text);
      setAttachment((current) => {
        if (current) {
          // Something new was attached while the send was in flight; the
          // failed message's thumbnail URL would otherwise leak.
          if (att?.previewUrl && att.previewUrl !== current.previewUrl) {
            URL.revokeObjectURL(att.previewUrl);
          }
          return current;
        }
        return att;
      });
      if (isRestricted(error)) {
        toast.error(t("chat.restricted"));
        return;
      }
      if (att) reportSendFailure(error, "file", "send", { type: att.contentType, size: att.byteSize });
      toast.error(t("chat.composer.sendFailed"));
    }
  };

  const canSend = (body.trim().length > 0 || !!attachment) && !loading;

  return (
    <div className="@container border-t border-border bg-background/95 px-4 pb-4 pt-3 backdrop-blur">
      {/* Reply preview */}
      {replyTarget && voiceMode === "idle" && (
        <div className="mb-2 flex items-start gap-2 rounded-xl border-l-[3px] border-primary bg-secondary/70 px-3 py-2">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold text-primary">
              {t("chat.composer.replyingTo", { name: replyTarget.sender.firstName })}
            </div>
            <p className="mt-0.5 line-clamp-1 text-[12px] text-muted-foreground">
              {replyTarget.body || t("chat.composer.attachment")}
            </p>
          </div>
          <button
            onClick={() => setReplyTarget(null)}
            className="rounded-full p-1 text-muted-foreground hover:bg-background focus-ring"
            aria-label={t("chat.composer.cancelReply")}
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      {/* Attachment preview chip */}
      {attachment && voiceMode === "idle" && (
        <div className="mb-2 flex items-center gap-2 rounded-xl border border-border bg-secondary/50 px-3 py-2">
          {attachment.kind === "image" && attachment.previewUrl ? (
            <>
              <img
                src={attachment.previewUrl}
                alt={attachment.filename}
                data-testid="attachment-thumbnail"
                className="size-12 shrink-0 rounded-lg border border-border object-cover"
              />
              <span className="flex-1" />
            </>
          ) : (
            <>
              <Paperclip className="size-3.5 text-muted-foreground" />
              <span className="flex-1 truncate text-[12px]">{attachment.filename}</span>
            </>
          )}
          <button
            onClick={discardAttachment}
            className="rounded-full p-1 text-muted-foreground hover:bg-background focus-ring"
            aria-label={t("chat.composer.removeAttachment")}
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      <div className="relative">
        {emojiOpen && voiceMode === "idle" && (
          <div ref={emojiPanelRef} className="absolute bottom-full right-0 mb-2 z-20">
            <EmojiPicker
              onEmojiClick={onEmojiSelect}
              theme={Theme.AUTO}
              emojiStyle={EmojiStyle.NATIVE}
              width={320}
              height={380}
              skinTonesDisabled
              searchDisabled={false}
              previewConfig={{ showPreview: false }}
              lazyLoadEmojis
            />
          </div>
        )}

        {/* Idle pill */}
        {voiceMode === "idle" && (
          <div className="flex items-end gap-2 rounded-2xl border border-border bg-secondary/50 px-2 py-1.5 focus-within:border-ring focus-within:bg-background focus-within:ring-2 focus-within:ring-ring/25">
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_MIME_TYPES.join(",")}
              className="hidden"
              onChange={onPickFile}
            />
            <IconBtn
              label={t("chat.composer.attachFile")}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || loading}
            >
              {uploading ? (
                <Loader2 className="size-[18px] animate-spin" />
              ) : (
                <Paperclip className="size-[18px]" />
              )}
            </IconBtn>

            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={(e) => {
                // Return sends on a computer. On a touch screen it adds a line (#1099): the phone keyboard has
                // no Shift+Return, and the Send button is next to the box.
                if (e.key === "Enter" && !e.shiftKey && !isTouchScreen()) {
                  e.preventDefault();
                  onSend();
                }
              }}
              onPaste={onPaste}
              rows={1}
              placeholder={t("chat.composer.placeholder")}
              className="scrollbar-thin max-h-32 min-h-[28px] min-w-0 flex-1 resize-none bg-transparent px-1 py-1.5 text-base leading-5 md:text-[14px] outline-none placeholder:text-muted-foreground"
            />

            <button
              ref={emojiButtonRef}
              type="button"
              aria-label={t("chat.composer.emoji")}
              onClick={() => setEmojiOpen((v) => !v)}
              className={cn(
                "hidden @[22rem]:inline-flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus-ring",
                emojiOpen && "text-primary",
              )}
            >
              <Smile className="size-[18px]" />
            </button>

            <IconBtn label={t("chat.composer.recordVoiceNote")} onClick={startRecording} className="hidden @[22rem]:inline-flex">
              <Mic className="size-[18px]" />
            </IconBtn>

            <button
              type="button"
              aria-label={t("chat.composer.send")}
              disabled={!canSend}
              onClick={onSend}
              className={cn(
                "inline-flex size-9 shrink-0 items-center justify-center rounded-full transition-all focus-ring",
                canSend
                  ? "bg-primary text-primary-foreground shadow-sm hover:brightness-105"
                  : "bg-secondary text-muted-foreground",
              )}
            >
              <Send className="size-4" />
            </button>
          </div>
        )}

        {/* Recording pill */}
        {voiceMode === "recording" && (
          <div className="flex h-[52px] items-center gap-3 rounded-2xl bg-destructive/10 px-3">
            <button
              type="button"
              onClick={cancelRecording}
              aria-label={t("chat.composer.cancelRecording")}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground focus-ring"
            >
              <Trash2 className="size-[18px]" />
            </button>
            <span className="size-2 shrink-0 rounded-full bg-destructive animate-pulse" />
            <span className="text-[13px] font-mono tabular-nums">
              {formatDuration(recordingSec)}
            </span>
            <span className="text-[11px] text-muted-foreground">
              / {formatDuration(MAX_VOICE_DURATION)}
            </span>
            <div className="flex flex-1 items-center justify-center gap-[3px] overflow-hidden">
              {Array.from({ length: 24 }).map((_, i) => (
                <span
                  key={i}
                  className="block w-[2px] rounded-full bg-destructive/70"
                  style={{
                    height: `${4 + Math.abs(Math.sin((i + recordingSec) * 0.7)) * 14}px`,
                    animation: "pulse-soft 1s ease-in-out infinite",
                    animationDelay: `${i * 40}ms`,
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={stopRecording}
              aria-label={t("chat.composer.stopRecording")}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground hover:brightness-105 focus-ring"
            >
              <span className="block size-3 rounded-sm bg-white" />
            </button>
          </div>
        )}

        {/* Preview pill (recorded, not yet sent) */}
        {voiceMode === "preview" && preview && (
          <div className="flex h-[52px] items-center gap-3 rounded-2xl border border-border bg-secondary/50 px-3">
            <button
              type="button"
              onClick={discardPreview}
              disabled={voiceUploading}
              aria-label={t("chat.composer.discardVoiceNote")}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-50 focus-ring"
            >
              <Trash2 className="size-[18px]" />
            </button>

            <button
              type="button"
              onClick={togglePreviewPlay}
              aria-label={
                previewPlaying
                  ? t("chat.composer.pauseVoiceNote")
                  : t("chat.composer.playVoiceNote")
              }
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground text-background hover:bg-foreground/85 focus-ring"
            >
              {previewPlaying ? (
                <Pause className="size-3.5 fill-current" />
              ) : (
                <Play className="size-3.5 fill-current" />
              )}
            </button>

            <div className="flex min-w-0 flex-1 items-center gap-[2px] overflow-hidden">
              {preview.waveform.map((amp, i) => {
                const heightPx = 6 + amp * 14;
                const isPast = i < Math.floor(preview.waveform.length * previewProgress);
                return (
                  <span
                    key={i}
                    className={cn(
                      "block w-[2px] rounded-full transition-colors",
                      isPast ? "bg-foreground" : "bg-foreground/40",
                    )}
                    style={{ height: `${heightPx}px` }}
                  />
                );
              })}
            </div>

            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {formatDuration(preview.durationSec)}
            </span>

            <button
              type="button"
              onClick={sendVoice}
              disabled={voiceUploading}
              aria-label={t("chat.composer.sendVoiceNote")}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm hover:brightness-105 disabled:opacity-50 focus-ring"
            >
              {voiceUploading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
            </button>

            <audio
              ref={previewAudioRef}
              src={preview.url}
              preload="metadata"
              onPlay={() => setPreviewPlaying(true)}
              onPause={() => setPreviewPlaying(false)}
              onEnded={() => {
                setPreviewPlaying(false);
                setPreviewProgress(0);
              }}
              onTimeUpdate={(e) => {
                const el = e.currentTarget;
                const denom =
                  el.duration && isFinite(el.duration) && el.duration > 0
                    ? el.duration
                    : preview.durationSec;
                if (denom > 0) setPreviewProgress(Math.min(1, el.currentTime / denom));
              }}
              className="sr-only"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function IconBtn({
  children,
  label,
  onClick,
  disabled,
  className,
}: {
  children: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background hover:text-foreground disabled:opacity-50 focus-ring",
        className,
      )}
    >
      {children}
    </button>
  );
}
